// ADR-035 "site map" cluster: the bank kind choice (form / list / detail badge) and the Site map tab of a
// navigation knowledge bank (src/pages/knowledge-banks/SiteMapTab.tsx). Spread into the central LABELS in
// labels.ts.
//
// The portal's `t()` takes no variables, so `{title}`, `{n}`, `{max}` and `{count}` are substituted by the
// caller with .replace(). Counted sentences have separate None / One / Many keys: Hebrew plural agreement
// cannot be done by string substitution.
//
// UI vocabulary: the wire values `page` / `category` are shown as "screen" / "menu group".
export const SITE_MAP_LABELS = {
  // ── Bank kind (create / edit form, list, detail header) ──────────────
  kbKindLabel: { en: 'Type', he: 'סוג' },
  kbKindStandard: { en: 'Standard knowledge', he: 'ידע רגיל' },
  kbKindNavigation: { en: 'Navigation & system map', he: 'ניווט ומפת מערכת' },
  kbKindNavigationHelp: {
    en: 'A navigation bank also holds a site map of the product’s screens, which the bot always reads in full.',
    he: 'מאגר ניווט מכיל גם מפת אתר של מסכי המוצר, שהבוט תמיד קורא במלואה.',
  },
  kbKindFixedNote: {
    en: 'Set when the bank was created; it cannot be changed.',
    he: 'נקבע בעת יצירת המאגר ולא ניתן לשנות אותו.',
  },
  kbKindBadgeNavigation: { en: 'Navigation', he: 'ניווט' },

  // ── Tab ────────────────────────────────────────────────────────────────
  kbDetailTabSiteMap: { en: 'Site map', he: 'מפת אתר' },

  // ── Tree ───────────────────────────────────────────────────────────────
  siteMapLoading: { en: 'Loading the site map…', he: 'טוען את מפת האתר…' },
  siteMapLoadError: { en: 'Failed to load the site map.', he: 'טעינת מפת האתר נכשלה.' },
  siteMapNotNavigation: {
    en: 'This bank has no site map. Only a navigation bank has one.',
    he: 'למאגר הזה אין מפת אתר. רק למאגר ניווט יש מפה.',
  },
  siteMapTreeTitle: { en: 'Screens and menus', he: 'מסכים ותפריטים' },
  siteMapAddTopLevel: { en: '+ Add top-level screen', he: '+ הוסף מסך ברמה העליונה' },
  siteMapEmptyTitle: { en: 'The site map is empty', he: 'מפת האתר ריקה' },
  siteMapEmptyBody: {
    en: 'Start with the screen people see first, usually the home page. Then add the menu groups and screens under it.',
    he: 'התחילו במסך שאנשים רואים ראשון, בדרך כלל מסך הבית. אחר כך הוסיפו מתחתיו את קבוצות התפריט והמסכים.',
  },
  siteMapLimitReached: {
    en: 'The site map holds at most {max} items. Delete some before adding more.',
    he: 'מפת האתר מכילה עד {max} פריטים. מחקו חלק לפני שתוסיפו עוד.',
  },
  siteMapItemCount: { en: '{n} items', he: '{n} פריטים' },
  siteMapTypeScreen: { en: 'Screen', he: 'מסך' },
  siteMapTypeMenuGroup: { en: 'Menu group', he: 'קבוצת תפריט' },
  siteMapHasPicture: { en: 'Has a screenshot', he: 'יש צילום מסך' },
  siteMapDisabledBadge: { en: 'Off', he: 'כבוי' },
  siteMapDisabledHint: {
    en: 'Turned off: the bot does not read this item or anything under it.',
    he: 'כבוי: הבוט לא קורא את הפריט הזה ואת כל מה שמתחתיו.',
  },
  siteMapDragHandleAria: { en: 'Drag to reorder {title}', he: 'גרור כדי לשנות את מיקום {title}' },
  siteMapMoveUp: { en: 'Move up', he: 'הזז למעלה' },
  siteMapMoveDown: { en: 'Move down', he: 'הזז למטה' },
  siteMapMoveUnder: { en: 'Move under…', he: 'העבר אל…' },
  siteMapAddScreenUnder: { en: '+ Screen', he: '+ מסך' },
  siteMapAddScreenUnderAria: { en: 'Add a screen under {title}', he: 'הוסף מסך תחת {title}' },
  siteMapAddGroupUnder: { en: '+ Menu group', he: '+ קבוצת תפריט' },
  siteMapAddGroupUnderAria: { en: 'Add a menu group under {title}', he: 'הוסף קבוצת תפריט תחת {title}' },
  siteMapTooDeepHint: {
    en: 'The site map is at most {max} levels deep.',
    he: 'מפת האתר מגיעה לעומק של {max} רמות לכל היותר.',
  },
  siteMapBusy: { en: 'Saving…', he: 'שומר…' },

  // ── Delete confirm ─────────────────────────────────────────────────────
  siteMapDeleteTitle: { en: 'Delete from the site map', he: 'מחיקה ממפת האתר' },
  siteMapDeleteConfirmNone: { en: 'Delete “{title}”?', he: 'למחוק את “{title}”?' },
  siteMapDeleteConfirmOne: {
    en: 'Delete “{title}”? The 1 item under it will also be deleted.',
    he: 'למחוק את “{title}”? גם הפריט האחד שמתחתיו יימחק.',
  },
  siteMapDeleteConfirmMany: {
    en: 'Delete “{title}”? The {count} items under it will also be deleted.',
    he: 'למחוק את “{title}”? גם {count} הפריטים שמתחתיו יימחקו.',
  },

  // ── Move under… picker ─────────────────────────────────────────────────
  siteMapMoveDialogTitle: { en: 'Move “{title}” under…', he: 'להעביר את “{title}” אל…' },
  siteMapMoveTopLevel: { en: '(Top level)', he: '(רמה עליונה)' },
  siteMapMoveParentLabel: { en: 'New place', he: 'מיקום חדש' },
  siteMapMoveSubmit: { en: 'Move', he: 'העבר' },
  siteMapMoveHelp: {
    en: 'It moves with everything under it and goes to the end of the list there.',
    he: 'הפריט עובר עם כל מה שמתחתיו ומתווסף לסוף הרשימה במקום החדש.',
  },

  // ── Node editor ────────────────────────────────────────────────────────
  siteMapEditorCreateTitle: { en: 'Add to the site map', he: 'הוספה למפת האתר' },
  siteMapEditorEditTitle: { en: 'Edit site map item', he: 'עריכת פריט במפת האתר' },
  siteMapEditorUnder: { en: 'Under: {path}', he: 'תחת: {path}' },
  siteMapEditorTopLevel: { en: 'At the top level', he: 'ברמה העליונה' },
  siteMapFieldType: { en: 'Type', he: 'סוג' },
  siteMapTypeScreenHelp: { en: 'A screen or page of the product', he: 'מסך או דף במוצר' },
  siteMapTypeMenuGroupHelp: {
    en: 'A menu or section that groups the screens under it',
    he: 'תפריט או אזור שמקבץ את המסכים שתחתיו',
  },
  siteMapFieldTitle: { en: 'Title', he: 'כותרת' },
  siteMapFieldTitleHelp: {
    en: 'The name the product shows for it, as users see it.',
    he: 'השם שהמוצר מציג, כפי שהמשתמשים רואים אותו.',
  },
  siteMapFieldDescription: { en: 'What it is for', he: 'למה זה משמש' },
  siteMapFieldUrl: { en: 'URL (optional)', he: 'כתובת (לא חובה)' },
  siteMapFieldEnabled: {
    en: 'On: the bot reads this item (turning it off also hides everything under it)',
    he: 'פעיל: הבוט קורא את הפריט הזה (כיבוי מסתיר גם את כל מה שמתחתיו)',
  },
  siteMapFieldPicture: { en: 'Screenshot', he: 'צילום מסך' },
  siteMapPictureSaveFirst: {
    en: 'Save the item first, then add a screenshot.',
    he: 'שמרו קודם את הפריט, ואז הוסיפו צילום מסך.',
  },
  siteMapPictureUpload: { en: 'Upload screenshot', he: 'העלה צילום מסך' },
  siteMapPictureReplace: { en: 'Replace screenshot', he: 'החלף צילום מסך' },
  siteMapPictureRemove: { en: 'Remove screenshot', he: 'הסר צילום מסך' },
  siteMapPictureDescribing: { en: 'Describing the screenshot…', he: 'מתאר את צילום המסך…' },
  siteMapPictureAlt: { en: 'Screenshot of {title}', he: 'צילום מסך של {title}' },
  siteMapPictureUnavailable: { en: '(preview unavailable)', he: '(התצוגה המקדימה לא זמינה)' },
  siteMapPictureNotDescribed: {
    en: 'The screenshot was saved but could not be described automatically. Write what the screen shows below.',
    he: 'צילום המסך נשמר אך לא ניתן היה לתאר אותו אוטומטית. כתבו למטה מה המסך מציג.',
  },
  siteMapPictureHint: { en: 'PNG, JPEG, WebP or GIF, up to 5 MB.', he: '‏PNG, ‏JPEG, ‏WebP או GIF, עד 5MB.' },
  siteMapFieldPictureDescription: { en: 'What the screen shows', he: 'מה המסך מציג' },
  siteMapFieldPictureDescriptionHelp: {
    en: 'The bot reads this text, not the picture itself.',
    he: 'הבוט קורא את הטקסט הזה, לא את התמונה עצמה.',
  },
  siteMapCreatedAddPicture: {
    en: 'Saved. You can add a screenshot now, or close.',
    he: 'נשמר. אפשר להוסיף עכשיו צילום מסך, או לסגור.',
  },
  siteMapEditorClose: { en: 'Close', he: 'סגור' },
  siteMapCharCount: { en: '{n} / {max}', he: '{n} / {max}' },

  // ── Validation ────────────────────────────────────────────────────────
  siteMapValTitleRequired: { en: 'Enter a title.', he: 'הזינו כותרת.' },
  siteMapValTitleTooLong: { en: 'The title is at most {max} characters.', he: 'הכותרת עד {max} תווים.' },
  siteMapValTextTooLong: { en: 'This text is at most {max} characters.', he: 'הטקסט עד {max} תווים.' },
  siteMapValUrl: {
    en: 'Enter a full web address starting with http:// or https://, or leave it empty.',
    he: 'הזינו כתובת מלאה שמתחילה ב‏-http://‏ או ‏https://‏, או השאירו ריק.',
  },
  siteMapValUrlTooLong: { en: 'The URL is at most {max} characters.', he: 'הכתובת עד {max} תווים.' },
  siteMapValPictureType: {
    en: 'Choose a PNG, JPEG, WebP or GIF image.',
    he: 'בחרו תמונה מסוג ‏PNG, ‏JPEG, ‏WebP או GIF.',
  },
  siteMapValPictureSize: { en: 'The screenshot is larger than 5 MB.', he: 'צילום המסך גדול מ‏-5MB.' },

  // ── Server errors ──────────────────────────────────────────────────────
  siteMapErrCycle: {
    en: 'An item cannot be moved under itself or under one of its own items.',
    he: 'לא ניתן להעביר פריט אל מתחת לעצמו או אל מתחת לאחד מהפריטים שלו.',
  },
  siteMapErrTooDeep: {
    en: 'That would make the site map deeper than {max} levels.',
    he: 'הפעולה הייתה הופכת את מפת האתר לעמוקה מ‏-{max} רמות.',
  },
  siteMapErrInvalidPicture: {
    en: 'That file is not a usable screenshot. Use a PNG, JPEG, WebP or GIF image up to 5 MB.',
    he: 'הקובץ אינו צילום מסך שאפשר להשתמש בו. השתמשו בתמונת ‏PNG, ‏JPEG, ‏WebP או GIF עד 5MB.',
  },
  siteMapErrNotFound: {
    en: 'That item no longer exists. The site map was reloaded.',
    he: 'הפריט כבר לא קיים. מפת האתר נטענה מחדש.',
  },
  siteMapErrGeneric: { en: 'The change could not be saved.', he: 'לא ניתן היה לשמור את השינוי.' },

  // ── Preview ────────────────────────────────────────────────────────────
  siteMapPreviewTitle: { en: 'What the bot reads', he: 'מה הבוט קורא' },
  siteMapPreviewHelp: {
    en: 'The exact text bots linked to this bank read in full on every turn, once the site map feature is switched on.',
    he: 'הטקסט המדויק שהבוטים המקושרים למאגר קוראים במלואו בכל תור, לאחר שתכונת מפת האתר מופעלת.',
  },
  siteMapPreviewCounter: { en: '{n} / {max} characters', he: '{n} / {max} תווים' },
  siteMapPreviewTruncated: {
    en: 'The site map is longer than the limit, so the bot reads only the text above. Shorten descriptions or turn off items it does not need.',
    he: 'מפת האתר ארוכה מהמגבלה, ולכן הבוט קורא רק את הטקסט שלמעלה. קצרו תיאורים או כבו פריטים שאין בהם צורך.',
  },
  siteMapPreviewEmpty: { en: '(nothing yet)', he: '(עדיין אין תוכן)' },
  siteMapPreviewFull: { en: 'Full map', he: 'מפה מלאה' },
  siteMapPreviewTree: { en: 'Tree', he: 'עץ' },
  siteMapPreviewTreeHelp: {
    en: 'In tree mode the bot reads this tree on every turn, and the full entries of only the screens the conversation is about.',
    he: 'במצב עץ הבוט קורא את העץ הזה בכל תור, ואת הפרטים המלאים רק של המסכים שהשיחה עוסקת בהם.',
  },

  // ── Tree mode: the one sentence per screen ─────────────────────────────
  siteMapFieldSummary: { en: 'One-sentence summary', he: 'תקציר במשפט אחד' },
  siteMapFieldSummaryHelp: {
    en: 'Shown next to the screen’s name in the tree the bot reads. Written by AI from the text above; you can edit it, and your text is kept.',
    he: 'מוצג ליד שם המסך בעץ שהבוט קורא. נכתב בידי AI מהטקסט שלמעלה; אפשר לערוך אותו, והטקסט שלכם נשמר.',
  },
  siteMapSummaryWriteWithAi: { en: 'Write with AI', he: 'כתיבה עם AI' },
  siteMapSummaryWriting: { en: 'Writing…', he: 'כותב…' },
  siteMapSummaryEditedByYou: { en: 'Written by an admin — AI will not change it.', he: 'נכתב בידי מנהל — ה-AI לא ישנה אותו.' },
  siteMapSummaryNotWritten: {
    en: 'The summary could not be written now. Try again, or write it yourself.',
    he: 'לא ניתן היה לכתוב את התקציר כעת. נסו שוב, או כתבו אותו בעצמכם.',
  },
  siteMapSummarySaveFirst: { en: 'Save the item first to write its summary with AI.', he: 'שמרו קודם את הפריט כדי לכתוב את התקציר עם AI.' },
  siteMapWriteMissingSummaries: { en: 'Write missing summaries', he: 'כתיבת תקצירים חסרים' },
  siteMapWritingSummaries: { en: 'Writing summaries…', he: 'כותב תקצירים…' },
  siteMapSummariesDone: {
    en: '{written} summaries written, {remaining} still missing.',
    he: 'נכתבו {written} תקצירים, {remaining} עדיין חסרים.',
  },

  // ── D8: view switch ─────────────────────────────────────────────────────
  siteMapViewLabel: { en: 'View', he: 'תצוגה' },
  siteMapViewList: { en: 'List', he: 'רשימה' },
  siteMapViewGraph: { en: 'Graph', he: 'גרף' },

  // ── D8: graph view ──────────────────────────────────────────────────────
  siteMapGraphAria: { en: 'Site map as a graph', he: 'מפת האתר כגרף' },
  siteMapGraphHelp: {
    en: 'Solid arrows are the menus. Dashed arrows are other ways to get from one screen to another. Drag the background to move around; scroll to zoom.',
    he: 'חיצים רציפים הם התפריטים. חיצים מקווקווים הם דרכים נוספות להגיע ממסך למסך. גררו את הרקע כדי לזוז, וגללו כדי להתקרב או להתרחק.',
  },
  siteMapZoomIn: { en: 'Zoom in', he: 'התקרב' },
  siteMapZoomOut: { en: 'Zoom out', he: 'התרחק' },
  siteMapZoomFit: { en: 'Fit', he: 'הצג הכול' },
  siteMapZoomFitAria: { en: 'Fit the whole map in view', he: 'הצג את כל המפה בחלון' },
  siteMapGraphSelected: { en: 'Actions for “{title}”', he: 'פעולות עבור “{title}”' },
  siteMapConnectTo: { en: 'Connect to…', he: 'חבר אל…' },
  siteMapConnectHint: {
    en: 'Click the screen this one leads to. Press Esc to cancel.',
    he: 'לחצו על המסך שאליו המסך הזה מוביל. לביטול לחצו Esc.',
  },
  siteMapConnectFrom: { en: 'Connecting from “{title}”', he: 'מחברים מ־“{title}”' },
  siteMapConnectSelf: {
    en: 'A screen cannot lead to itself. Click a different one.',
    he: 'מסך לא יכול להוביל לעצמו. לחצו על מסך אחר.',
  },
  siteMapConnectTooFew: {
    en: 'Add at least two items before connecting them.',
    he: 'הוסיפו לפחות שני פריטים לפני שמחברים ביניהם.',
  },
  siteMapLinksLimitReached: {
    en: 'The site map holds at most {max} connections. Delete some before adding more.',
    he: 'מפת האתר מכילה עד {max} חיבורים. מחקו חלק לפני שתוסיפו עוד.',
  },
  siteMapLinkCount: { en: '{n} connections', he: '{n} חיבורים' },

  // ── D8: connection dialog / popover ─────────────────────────────────────
  siteMapLinkDialogTitle: { en: 'Connect “{from}” to “{to}”', he: 'חיבור “{from}” אל “{to}”' },
  siteMapLinkDialogTitleAny: { en: 'Connect “{from}” to…', he: 'חיבור “{from}” אל…' },
  siteMapLinkDialogHelp: {
    en: 'Use this when a button or link on this screen opens another screen. The bot can then offer the shortest route.',
    he: 'השתמשו בזה כשכפתור או קישור במסך הזה פותח מסך אחר. כך הבוט יוכל להציע את הדרך הקצרה ביותר.',
  },
  siteMapLinkTarget: { en: 'Leads to', he: 'מוביל אל' },
  siteMapLinkTargetPlaceholder: { en: 'Choose a screen…', he: 'בחרו מסך…' },
  siteMapLinkTargetRequired: { en: 'Choose where it leads.', he: 'בחרו לאן זה מוביל.' },
  siteMapLinkNoTargets: {
    en: 'This item already leads to every other item.',
    he: 'הפריט הזה כבר מוביל לכל שאר הפריטים.',
  },
  siteMapLinkLabel: { en: 'How do you get there?', he: 'איך מגיעים לשם?' },
  siteMapLinkLabelPlaceholder: { en: 'e.g. the button’s name', he: 'למשל, שם הכפתור' },
  siteMapLinkLabelHelp: {
    en: 'Optional. The bot reads it when it explains the way.',
    he: 'לא חובה. הבוט קורא את זה כשהוא מסביר את הדרך.',
  },
  siteMapLinkSave: { en: 'Connect', he: 'חבר' },
  siteMapLinkSaveLabel: { en: 'Save', he: 'שמור' },
  siteMapLinkPopoverTitle: { en: 'Connection', he: 'חיבור' },
  siteMapLinkDelete: { en: 'Delete connection', he: 'מחק חיבור' },
  siteMapLinkNoLabel: { en: '(no description)', he: '(ללא תיאור)' },
  siteMapLinkAria: {
    en: 'Connection from {from} to {to}',
    he: 'חיבור מ־{from} אל {to}',
  },
  siteMapLinkRemoveAria: { en: 'Remove the connection to {title}', he: 'הסר את החיבור אל {title}' },
  // The arrow points from this item to the target in reading order: right in English, left in Hebrew.
  siteMapLinkOutChip: { en: '→ {title}', he: '← {title}' },
  siteMapLinkInChip: { en: 'Reached from {title}', he: 'מגיעים מ־{title}' },
  siteMapLinksSrHeading: { en: 'Connections between screens', he: 'חיבורים בין מסכים' },
  siteMapLinksSrNone: { en: 'No connections yet.', he: 'אין עדיין חיבורים.' },
  siteMapLinkSrItem: { en: '{from} leads to {to}', he: '{from} מוביל אל {to}' },

  // ── D8: server errors ───────────────────────────────────────────────────
  siteMapErrDuplicateLink: {
    en: 'These two screens are already connected.',
    he: 'שני המסכים האלה כבר מחוברים.',
  },
  siteMapErrTooManyLinks: {
    en: 'The site map holds at most {max} connections. Delete some before adding more.',
    he: 'מפת האתר מכילה עד {max} חיבורים. מחקו חלק לפני שתוסיפו עוד.',
  },
  siteMapErrLinkLabel: {
    en: 'The description is at most {max} characters.',
    he: 'התיאור עד {max} תווים.',
  },
  siteMapErrLinkEnds: {
    en: 'Choose two different items of this site map.',
    he: 'בחרו שני פריטים שונים של מפת האתר הזו.',
  },
  siteMapErrInvalidField: {
    en: 'One of the values is not valid ({field}).',
    he: 'אחד הערכים אינו תקין ({field}).',
  },
} satisfies Record<string, { en: string; he: string }>;
